import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
  Paper
} from '@mui/material';
import { useUserSession } from '../contexts/UserContext';
import authApi from '../services/authApi';
import axios from 'axios';
import NavigationDrawer from './Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';
import { useSharePoint } from '../contexts/SharePointContext';

const FileManagement = () => {
  const { userSession } = useUserSession();
  const { selectedConfig, refreshTrigger } = useSharePoint();
  
  const [libraries, setLibraries] = useState([]);
  const [selectedLibrary, setSelectedLibrary] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setSelectedLibrary("");

    const fetchLibraries = async () => {
      setLoading(true);
      try {
        const token = authApi.getToken();
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/sharepoint/document-libraries`,
          {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${token}`
            },
            params: {
              configId: selectedConfig
            }
          }
        );
        
        setLibraries(response.data);
      } catch (error) {
        console.error('Error fetching document libraries:', error);
        setError('Failed to fetch document libraries');
      } finally {
        setLoading(false);
      }
    };

    if (userSession?.email && selectedConfig) {
      fetchLibraries();
    }
  }, [userSession?.email, userSession?.impersonatedConfigId, selectedConfig, refreshTrigger]);

  const handleGenerateReport = async () => {
    if (!selectedLibrary || generating) return;

    setGenerating(true);
    setError(null);

    try {
      const token = authApi.getToken();

      if (sendEmail) {
        alert('Emailing the report is not supported with the new DB report. Please download the report.');
      } else {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/datawarehouse/file-management`,
        {
            libraryIds: [selectedLibrary],
          configId: selectedConfig
        },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`
          },
          responseType: 'blob'
        }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `FileManagementReport_${new Date().toLocaleDateString()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setError('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  if (!selectedConfig) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
        <NavigationDrawer
          isMobile={isMobile}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          activeSection={''}
          setActiveSection={() => {}}
          navigate={navigate}
        />
        
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Typography variant="h4" gutterBottom>
            File Management Report
          </Typography>
          <Typography variant="body1" color="error">
            Loading SharePoint configuration...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={''}
        setActiveSection={() => {}}
        navigate={navigate}
      />
      
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Typography variant="h4" gutterBottom>
          File Management Report
          </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(244, 81, 30, 0.06)' }}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            What this report is for
          </Typography>
          <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
            The File Management report is an <strong>Excel workbook</strong> about documents in a single library:
            locations, metadata, and related details from the data warehouse. It supports cleanup, migration planning,
            and understanding what is stored where.
          </Typography>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            How to run it
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <Typography component="li" variant="body2">
              Select the site in the header, choose one <strong>document library</strong>, then click{' '}
              <strong>Generate Report</strong> to download the spreadsheet.
            </Typography>
            <Typography component="li" variant="body2">
              <strong>Send report via email</strong> is not available for this warehouse-based report; leave it off and
              use the download.
            </Typography>
          </Box>
        </Paper>

        {loading && <Typography>Loading libraries...</Typography>}

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Document Library</InputLabel>
          <Select
            value={selectedLibrary}
            onChange={(e) => setSelectedLibrary(e.target.value)}
            disabled={loading}
          >
            {libraries.map((library) => (
              <MenuItem key={library.id} value={library.id}>
                {library.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
            />
          }
          label="Send report via email"
        />

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
            onClick={handleGenerateReport}
            disabled={!selectedLibrary || generating}
            startIcon={generating ? <CircularProgress size={20} /> : null}
          >
            {generating ? 'Generating...' : 'Generate Report'}
              </Button>
            </Box>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default FileManagement;

